/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  if (customElements.get('main-product')) return;

  const SIZE_RE = /^(\d+(?:\.\d+)?)L"x(\d+(?:\.\d+)?)H"$/;

  const motionOn = () =>
    window.gsap && document.documentElement.classList.contains('motion');

  const money = (cents) =>
    window.AirX && window.AirX.formatMoney
      ? window.AirX.formatMoney(cents)
      : '$' + (cents / 100).toFixed(2);

  const cleanSize = (value) => {
    const m = String(value || '').match(SIZE_RE);
    return m ? `${m[1]}″ × ${m[2]}″` : String(value || '');
  };

  class MainProduct extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.lowStock = parseInt(this.dataset.lowStock, 10) || 0;

      this.data = this.readJSON('[data-product-data]') || { options: [], variants: [] };
      this.strings = this.readJSON('[data-product-strings]') || {};

      this.section = this.closest('.main-product') || this;
      this.form = document.getElementById('product-form-' + this.sectionId);

      this.priceCurrent = this.querySelector('[data-price-current]');
      this.priceCompare = this.querySelector('[data-price-compare]');
      this.priceSr = this.querySelector('[data-price-sr]');
      this.atcButton = this.querySelector('[data-atc-button]');
      this.atcLabel = this.querySelector('[data-atc-label]');
      this.atcPrice = this.querySelector('[data-atc-price]');
      this.stockHint = this.querySelector('[data-stock-hint]');
      this.qtyInput = this.querySelector('[data-quantity-input]');

      this.stickyBar = this.section.querySelector('[data-sticky-bar]');
      this.stickyPrice = this.section.querySelector('[data-sticky-price]');
      this.stickyAtc = this.section.querySelector('[data-sticky-atc]');
      this.stickyAtcLabel = this.section.querySelector('[data-sticky-atc-label]');

      this.variant = this.resolveVariant() || this.data.variants[0] || null;
      this.renderedPrice = this.variant ? this.variant.price : 0;

      this.bindOptions();
      this.bindQuantity();
      this.bindRating();
      this.initStickyBar();

      if (this.variant && window.AirX) {
        window.AirX.emit('variant:change', {
          variant: this.variant,
          sectionId: this.sectionId,
          initial: true,
        });
      }
    }

    readJSON(selector) {
      const el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (err) {
        return null;
      }
    }

    selections() {
      const picked = [];
      this.querySelectorAll('[data-option-input]:checked').forEach((input) => {
        picked[Number(input.dataset.optionIndex)] = input.dataset.optionValue;
      });
      return picked;
    }

    resolveVariant(selections) {
      const picked = selections || this.selections();
      if (!picked.length) return this.data.variants[0] || null;
      return (
        this.data.variants.find((v) =>
          v.options.every((opt, i) => picked[i] == null || opt === picked[i])
        ) || null
      );
    }

    variantFor(index, value) {
      const picked = this.selections();
      picked[index] = value;
      return this.resolveVariant(picked);
    }

    bindOptions() {
      this.querySelectorAll('[data-option-input]').forEach((input) => {
        input.addEventListener('change', () => this.onVariantChange());
      });
    }

    onVariantChange() {
      this.variant = this.resolveVariant();
      this.updatePrice();
      this.updateHiddenId();
      this.updateSelectedLabels();
      this.updateDeltas();
      this.updateAvailabilityStates();
      this.updateAtc();
      this.updateStock();
      this.updateUrl();
      if (window.AirX) {
        window.AirX.emit('variant:change', {
          variant: this.variant,
          sectionId: this.sectionId,
        });
      }
    }

    updateHiddenId() {
      if (!this.variant || !this.form) return;
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) idInput.value = this.variant.id;
    }

    updateUrl() {
      if (!this.variant || !window.history.replaceState) return;
      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set('variant', this.variant.id);
      window.history.replaceState({}, '', url.pathname + url.search);
    }

    updateSelectedLabels() {
      this.querySelectorAll('[data-option-selected]').forEach((el) => {
        const index = Number(el.dataset.optionSelected);
        const checked = this.querySelector(
          `[data-option-input][data-option-index="${index}"]:checked`
        );
        if (!checked) return;
        const raw = checked.dataset.optionValue;
        if (SIZE_RE.test(raw)) {
          el.textContent = cleanSize(raw);
        } else if (/3\/4/.test(raw)) {
          el.textContent = this.strings.depthSlim || raw;
        } else if (/1 1\/2/.test(raw)) {
          el.textContent = this.strings.depthGallery || raw;
        } else {
          el.textContent = raw;
        }
      });

    }

    updatePrice() {
      if (!this.variant || !this.priceCurrent) return;
      const from = this.renderedPrice;
      const to = this.variant.price;
      this.renderedPrice = to;

      if (motionOn() && from !== to) {
        const state = { v: from };
        window.gsap.to(state, {
          v: to,
          duration: 0.5,
          ease: 'power2.out',
          onUpdate: () => {
            this.priceCurrent.textContent = money(Math.round(state.v));
          },
          onComplete: () => {
            this.priceCurrent.textContent = money(to);
          },
        });
        window.gsap.fromTo(
          this.priceCurrent,
          { y: 6, opacity: 0.4 },
          { y: 0, opacity: 1, duration: 0.45, ease: 'power3.out', clearProps: 'transform,opacity' }
        );
      } else {
        this.priceCurrent.textContent = money(to);
      }

      const onSale =
        this.variant.compare_at_price && this.variant.compare_at_price > to;
      this.priceCurrent.classList.toggle('price__sale', Boolean(onSale));
      if (this.priceCompare) {
        this.priceCompare.hidden = !onSale;
        if (onSale) this.priceCompare.textContent = money(this.variant.compare_at_price);
      }
      if (this.priceSr) {
        this.priceSr.textContent = onSale
          ? this.strings.salePrice || 'Sale price'
          : this.strings.regularPrice || 'Regular price';
      }
      this.updateAtcPrice();
      if (this.stickyPrice && this.variant) this.stickyPrice.textContent = money(to);
    }

    updateAtcPrice() {
      if (!this.atcPrice || !this.variant) return;
      const qty = Math.max(1, parseInt(this.qtyInput && this.qtyInput.value, 10) || 1);
      this.atcPrice.textContent = money(this.variant.price * qty);
    }

    updateDeltas() {
      if (!this.variant) return;
      this.querySelectorAll('[data-size-delta]').forEach((el) => {
        const index = Number(el.dataset.optionIndex);
        const value = el.dataset.sizeValue;
        const target = this.variantFor(index, value);
        if (!target || value === this.variant.options[index]) {
          el.textContent = '';
          return;
        }
        const delta = target.price - this.variant.price;
        if (!delta) {
          el.textContent = '';
        } else if (delta > 0) {
          el.textContent = '+' + money(delta);
        } else {
          el.textContent = '−' + money(-delta);
        }
      });
    }

    updateAvailabilityStates() {
      this.querySelectorAll('[data-option-input]').forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        const target = this.variantFor(index, input.dataset.optionValue);
        const label = this.querySelector(`label[for="${input.id}"]`);
        if (!label) return;
        label.classList.toggle('is-unavailable', !target);
        label.classList.toggle('is-soldout', Boolean(target && !target.available));
      });
    }

    updateAtc() {
      const v = this.variant;
      const soldOut = v && !v.available;
      const unavailable = !v;
      const label = unavailable
        ? this.strings.unavailable || 'Unavailable'
        : soldOut
          ? this.strings.soldOut || 'Sold out'
          : this.strings.addToCart || 'Add to gallery wall';

      if (this.atcButton) {
        this.atcButton.disabled = unavailable || soldOut;
        if (this.atcLabel) this.atcLabel.textContent = label;
        if (this.atcPrice) this.atcPrice.hidden = unavailable || soldOut;
      }
      if (this.stickyAtc) {
        this.stickyAtc.disabled = unavailable || soldOut;
        if (this.stickyAtcLabel) this.stickyAtcLabel.textContent = label;
      }
    }

    updateStock() {
      if (!this.stockHint) return;
      const v = this.variant;
      if (!v || !v.available) {
        this.stockHint.innerHTML = '';
        return;
      }
      const tracked =
        v.inventory_management === 'shopify' && v.inventory_policy === 'deny';
      if (
        tracked &&
        this.lowStock > 0 &&
        v.inventory_quantity > 0 &&
        v.inventory_quantity < this.lowStock
      ) {
        const span = document.createElement('span');
        span.className = 'buy-box__stock-urgent';
        span.textContent = (this.strings.onlyLeft || 'Only [count] left').replace(
          '[count]',
          v.inventory_quantity
        );
        this.stockHint.replaceChildren(span);
      } else {
        const span = document.createElement('span');
        span.className = 'buy-box__stock-calm';
        span.textContent = this.strings.shipsFree || '';
        this.stockHint.replaceChildren(span);
      }
    }

    bindQuantity() {
      if (!this.qtyInput) return;
      this.qtyInput.addEventListener('change', () => this.updateAtcPrice());
    }

    bindRating() {
      const row = this.querySelector('[data-rating-row]');
      if (!row) return;

      const countEl = row.querySelector('[data-rating-count]');

      if (countEl && this.strings.reviews) {
        const raw = countEl.textContent.replace(/[^\d]/g, '');
        if (raw) {
          countEl.textContent = this.strings.reviews.replace(
            '[count]',
            Number(raw).toLocaleString()
          );
        }
      }

      row.addEventListener('click', () => {
        if (window.AirxReviews && window.AirxReviews.scrollToReviews) {
          window.AirxReviews.scrollToReviews();
          return;
        }
        const target = document.getElementById('airx-reviews');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.onReviewStats = (e) => {
        const d = e.detail || {};
        if (!d.count) return;
        const stars = row.querySelector('.rating-stars');
        const avgEl = row.querySelector('[data-rating-average]');
        if (stars) stars.style.setProperty('--rating-pct', d.avg * 20 + '%');
        if (avgEl) avgEl.textContent = d.avg.toFixed(1);
        if (countEl && this.strings.reviews) {
          countEl.textContent = this.strings.reviews.replace(
            '[count]',
            d.count.toLocaleString()
          );
        }
      };
      document.addEventListener('airx:reviews:stats', this.onReviewStats);
    }

    initStickyBar() {
      if (!this.stickyBar || !this.atcButton || !('IntersectionObserver' in window)) return;
      this.stickyObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.stickyBar.classList.toggle('is-visible', passed);
          this.stickyBar.setAttribute('aria-hidden', String(!passed));
        },
        { threshold: 0 }
      );
      this.stickyObserver.observe(this.atcButton);
    }

    disconnectedCallback() {
      if (this.stickyObserver) this.stickyObserver.disconnect();
      if (this.onReviewStats) {
        document.removeEventListener('airx:reviews:stats', this.onReviewStats);
      }
    }
  }

  customElements.define('main-product', MainProduct);
})();
